import React, { useState } from "react";
import { X, ChevronDown } from "lucide-react";
import CatalogueBackground from "../../../../src/assets/images/download-cutsheet2.png";
import dashboardData from "../../../../src/data/tutorials/dashboard.json";
import videoData from "../../../../src/data/tutorials/video.json";
import accessData from "../../../../src/data/tutorials/access.json";
import scheduleData from "../../../../src/data/tutorials/schedule.json";
import reportData from "../../../../src/data/tutorials/report.json";
import maintenanceData from "../../../../src/data/tutorials/maintenance.json";
import deviceData from "../../../../src/data/tutorials/device.json";
import externalData from "../../../../src/data/tutorials/external.json";
import webUserData from "../../../../src/data/tutorials/web-user.json";
import serviceData from "../../../../src/data/tutorials/service.json";
import systemData from "../../../../src/data/tutorials/system.json";
import DashboardIcon from "../../../../src/assets/svg/MenuColorIcons/DashboardIcon";
import VideoIcon from "../../../../src/assets/svg/MenuColorIcons/VideoIcon";
import AccessIcon from "../../../../src/assets/svg/MenuColorIcons/AccessIcon";
import ScheduleIcon from "../../../../src/assets/svg/MenuColorIcons/ScheduleIcon";
import ReportIcon from "../../../../src/assets/svg/MenuColorIcons/ReportIcon";
import MaintenanceIcon from "../../../../src/assets/svg/MenuColorIcons/MaintenanceIcon";
import DeviceIcon from "../../../../src/assets/svg/MenuColorIcons/DeviceIcon";
import ExternalIcon from "../../../../src/assets/svg/MenuColorIcons/ExternalIcon";
import WebUserIcon from "../../../../src/assets/svg/MenuColorIcons/WebUserIcon";
import ServiceIcon from "../../../../src/assets/svg/MenuColorIcons/ServiceIcon";
import SystemIcon from "../../../../src/assets/svg/MenuColorIcons/SystemIcon";
import YouTubeIcon from "./../../../assets/images/tuitorials/YouTube";

const sections = [
  dashboardData,
  videoData,
  accessData,
  scheduleData,
  reportData,
  maintenanceData,
  deviceData,
  externalData,
  webUserData,
  serviceData,
  systemData,
];

const BASE_URL = "https://sicunet.com";
const API_URL = `${BASE_URL}/api`;
const UPLOADS_URL = `${BASE_URL}/uploads/`;

const fetcher = async (endpoint) => {
  const cleanEndpoint = endpoint.startsWith("/") ? endpoint : `/${endpoint}`;
  const url = `${API_URL}${cleanEndpoint}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

const TuitorialLibraryInfo = () => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [catalogues, setCatalogues] = useState([]);
  const [cataloguesLoading, setCataloguesLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [expandedSubItem, setExpandedSubItem] = useState(null);

  // Fetch catalogues on mount
  React.useEffect(() => {
    fetcher("catalogues/")
      .then((data) => {
        setCatalogues(data);
        setCataloguesLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching catalogues:", error);
        setCataloguesLoading(false);
      });
  }, []);

  // Auto-hide error message after 5 seconds
  React.useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  // Map of custom icon components
  const iconMap = {
    Dashboard: DashboardIcon,
    Video: VideoIcon,
    Access: AccessIcon,
    Schedule: ScheduleIcon,
    Report: ReportIcon,
    Maintenance: MaintenanceIcon,
    Device: DeviceIcon,
    External: ExternalIcon,
    "Web User": WebUserIcon,
    Service: ServiceIcon,
    System: SystemIcon,
  };

  const getIcon = (title) => {
    const IconComponent = iconMap[title];
    return IconComponent ? <IconComponent className="w-5 h-5" /> : null;
  };

  const getEmbedUrl = (url) => {
    if (!url) return "";

    // YouTube
    if (url.includes("youtube.com") || url.includes("youtu.be")) {
      const videoId = url.includes("youtu.be")
        ? url.split("youtu.be/")[1]?.split("?")[0]
        : url.split("v=")[1]?.split("&")[0];
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // Google Drive
    if (url.includes("drive.google.com")) {
      const fileId =
        url.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1] ||
        url.match(/id=([a-zA-Z0-9_-]+)/)?.[1];
      return `https://drive.google.com/file/d/${fileId}/preview`;
    }

    // Vimeo
    if (url.includes("vimeo.com")) {
      const videoId = url.split("vimeo.com/")[1]?.split("?")[0];
      return `https://player.vimeo.com/video/${videoId}`;
    }

    return url;
  };

  const handleSubItemToggle = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    const currentItem = sections[sectionIdx]?.items[itemIdx];

    if (!currentItem) return;

    // If NO videos → show error message (but allow click)
    if (currentItem.videos.length === 0) {
      setErrorMessage(
        `No tutorial video available for "${currentItem.label}" yet.`
      );
      return;
    }

    // If HAS exactly ONE video → open video directly
    if (currentItem.videos.length === 1) {
      handleVideoClick(currentItem.videos[0]);
      return;
    }

    // If HAS multiple videos → toggle expand/collapse
    if (expandedSubItem === key) {
      setExpandedSubItem(null);
    } else {
      setExpandedSubItem(key);
    }
  };

  const handleVideoClick = (video) => {
    if (video.videoUrl) {
      setSelectedVideo(video);
    } else {
      setErrorMessage(`Video "${video.name}" is not available yet.`);
    }
  };

  const closeModal = () => {
    setSelectedVideo(null);
  };

  const handleDownloadCatalogue = (catalogue) => {
    const cleanPath = catalogue.catalogue_file_link.replace(/^\/+/, "");
    const downloadUrl = `${UPLOADS_URL}${cleanPath}`;
    window.open(downloadUrl, "_blank");
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
          Learn how Neptune 2.0 works
        </h1>
        <p className="text-gray-600  mx-auto text-base">
          This video demonstrates how to unlock doors using credential cards and
          how administrators can add users, assign credentials, and manage door
          access through the Neptune 2.0 browser-based software.
        </p>
      </div>

      {/* Catalogue Section */}
      <div className="mb-8 rounded-lg overflow-hidden shadow-md">
        <div className="flex flex-col lg:flex-row bg-[#F8F8F8] rounded-md overflow-hidden">
          {/* Left side - Image */}
          <div className="flex bg-[#E6E6E6] w-full md:w-[620px] px-4 md:px-[220px] py-8 md:py-0 justify-center items-center">
            <img
              src={CatalogueBackground}
              alt="SMART & SIMPLE NEPTUNE 2.0"
              className="w-full h-auto max-w-[240px] object-contain"
            />
          </div>

          {/* Right side - Content */}
          <div className="w-full lg:w-1/2 p-6 md:p-10 flex flex-col justify-center">
            <h2 className="text-[18px] md:text-[22px] font-semibold text-[#121212] font-['Poppins'] mb-3">
              Catalog
            </h2>
            <p className="text-[#4A4C56] font-['Poppins'] mb-6 text-[14px]">
              Company Brochure - Sales Presentation
            </p>

            {cataloguesLoading ? (
              <div className="flex items-center gap-2 text-gray-500">
                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                <span>Loading Catalog...</span>
              </div>
            ) : catalogues.length > 0 ? (
              <div className="flex flex-col gap-2 w-full">
                {catalogues.map((catalogue) => (
                  <button
                    key={catalogue.id}
                    onClick={() => handleDownloadCatalogue(catalogue)}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#016FFC] hover:bg-[rgb(2,95,218)] text-white rounded-md transition-colors font-['Poppins'] font-medium text-[14px] leading-[20px] w-fit"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="flex-shrink-0"
                    >
                      <path
                        d="M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M4.66602 6.66602L7.99935 9.99935L11.3327 6.66602"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M8 10V2"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <span>Download </span>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-gray-500 text-sm">
                No catalogues available
              </div>
            )}
          </div>
        </div>
      </div>

      {/* All Video Tutorials Heading */}
      <h2 className="text-xl md:text-2xl font-bold text-center pt-3 text-gray-900 mb-8">
        All Video Tutorials
      </h2>

      {/* Main Sections - Horizontal Layout */}
      <div className="space-y-8  pl-4">
        {/* First Row - 6 sections */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-10">
          {sections.slice(0, 6).map((section, sectionIdx) => (
            <div key={sectionIdx} className="flex flex-col">
              {/* Section Header */}
              <div className="flex items-center gap-2 mb-3">
                <div className="flex-shrink-0">{getIcon(section.title)}</div>
                <h3 className="text-sm font-semibold text-gray-900">
                  {section.title}
                </h3>
              </div>

              {/* Section Items */}
              <div className="flex flex-col gap-1.5">
                {section.items.map((item, itemIdx) => {
                  const hasVideos = item.videos.length > 0;
                  const isSingleVideo = item.videos.length === 1;
                  const isExpanded =
                    expandedSubItem === `${sectionIdx}-${itemIdx}`;

                  return (
                    <div key={itemIdx}>
                      <button
                        onClick={() => handleSubItemToggle(sectionIdx, itemIdx)}
                        className="w-full flex items-center justify-between text-left text-[13px] text-gray-800 hover:text-blue-600 transition-colors"
                      >
                        <span className="pr-1">{item.label}</span>
                        {/* Show YouTube icon for single video, ChevronDown for multiple videos */}
                        {isSingleVideo ? (
                          <div className="flex items-center justify-center flex-shrink-0 pr-6">
                            <YouTubeIcon className="w-4 h-4" />
                          </div>
                        ) : hasVideos && !isSingleVideo ? (
                          <ChevronDown
                            className={`w-3 h-3 text-gray-500 transition-transform duration-300 flex-shrink-0 ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                          />
                        ) : null}
                      </button>

                      {/* Videos - only show for multiple videos when expanded */}
                      {isExpanded && hasVideos && !isSingleVideo && (
                        <div className="pl-3 mt-1 flex flex-col gap-1">
                          {item.videos.map((video, videoIdx) => (
                            <button
                              key={videoIdx}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleVideoClick(video);
                              }}
                              className="w-full flex items-center justify-between text-left text-xs text-gray-600 hover:text-blue-600 transition-colors group"
                            >
                              <span className="pr-1">{video.name}</span>
                              <div className="flex items-center justify-center flex-shrink-0 pr-6">
                                <YouTubeIcon className="w-3.5 h-3.5" />
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Second Row - Remaining sections */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {sections.slice(6).map((section, idx) => {
            const sectionIdx = idx + 6;
            return (
              <div key={sectionIdx} className="flex flex-col">
                {/* Section Header */}
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex-shrink-0">{getIcon(section.title)}</div>
                  <h3 className="text-sm font-semibold text-gray-900">
                    {section.title}
                  </h3>
                </div>

                {/* Section Items */}
                <div className="flex flex-col gap-2.5">
                  {section.items.map((item, itemIdx) => {
                    const hasVideos = item.videos.length > 0;
                    const isSingleVideo = item.videos.length === 1;
                    const isExpanded =
                      expandedSubItem === `${sectionIdx}-${itemIdx}`;

                    return (
                      <div key={itemIdx}>
                        <button
                          onClick={() =>
                            handleSubItemToggle(sectionIdx, itemIdx)
                          }
                          className="w-full flex items-center justify-between text-left text-[13px] text-gray-800 hover:text-blue-600 transition-colors"
                        >
                          <span className="pr-1">{item.label}</span>
                          {/* Show YouTube icon for single video, ChevronDown for multiple videos */}
                          {isSingleVideo ? (
                            <div className="flex items-center justify-center flex-shrink-0 pr-6">
                              <YouTubeIcon className="w-4 h-4" />
                            </div>
                          ) : hasVideos && !isSingleVideo ? (
                            <ChevronDown
                              className={`w-3 h-3 text-gray-500 transition-transform duration-300 flex-shrink-0 ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            />
                          ) : null}
                        </button>

                        {/* Videos - only show for multiple videos when expanded */}
                        {isExpanded && hasVideos && !isSingleVideo && (
                          <div className="pl-3 mt-1 flex flex-col gap-1">
                            {item.videos.map((video, videoIdx) => (
                              <button
                                key={videoIdx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleVideoClick(video);
                                }}
                                className="w-full flex items-center justify-between text-left text-xs text-gray-600 hover:text-blue-600 transition-colors group"
                              >
                                <span className="pr-1">{video.name}</span>
                                <div className="flex items-center justify-center flex-shrink-0 pr-6">
                                  <YouTubeIcon className="w-3.5 h-3.5" />
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-[7px] border-b">
              <h2 className="text-[18px] font-[500px] text-gray-700">
                {selectedVideo.name}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            <div className="relative w-full pt-[56.25%]">
              {selectedVideo.videoUrl ? (
                <iframe
                  src={getEmbedUrl(selectedVideo.videoUrl)}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={selectedVideo.name}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <p className="text-gray-500 text-lg">No video URL provided</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Error Message Toast */}
      {errorMessage && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 max-w-md w-full px-4">
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg shadow-lg p-4 flex items-start gap-3">
            <svg
              className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2h-1V9z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm font-medium text-red-800 flex-1">
              {errorMessage}
            </p>
            <button
              onClick={() => setErrorMessage(null)}
              className="text-red-600 hover:text-red-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TuitorialLibraryInfo;
